import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Plus, Star } from "lucide-react";
import { useProduct } from "@/components/pdp/PdpDataContext";

export const YouMayAlsoLove = () => {
  const { alsoLove, mediaRadius } = useProduct();
  const trackRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  // Mouse-drag scrolling (works on desktop like a swipe)
  const drag = useRef({ down: false, startX: 0, startScroll: 0, moved: false });

  const updateArrows = () => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, []);

  const scrollByCards = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector("[data-card]");
    const step = card ? card.getBoundingClientRect().width + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const onPointerDown = (e) => {
    const el = trackRef.current;
    if (!el) return;
    drag.current = { down: true, startX: e.clientX, startScroll: el.scrollLeft, moved: false };
    el.setPointerCapture?.(e.pointerId);
    el.classList.add("cursor-grabbing");
  };
  const onPointerMove = (e) => {
    if (!drag.current.down) return;
    const el = trackRef.current;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > 4) drag.current.moved = true;
    el.scrollLeft = drag.current.startScroll - dx;
  };
  const endDrag = (e) => {
    const el = trackRef.current;
    drag.current.down = false;
    el?.classList.remove("cursor-grabbing");
    try { el?.releasePointerCapture?.(e.pointerId); } catch {}
  };
  const onClickCapture = (e) => {
    // Prevent link/button clicks after a drag
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = false;
    }
  };

  return (
    <section className="bg-riversol-paper py-20 lg:py-32" data-testid="you-may-also-love">
      <div className="max-w-[1600px] mx-auto px-4 lg:px-8">
        <div className="flex items-end justify-between mb-10 lg:mb-12 gap-6 lg:gap-8">
          <div>
            <div className="pdp-eyebrow font-mono text-[11px] tracking-[0.24em] uppercase text-riversol-blue mb-4">
              {alsoLove.eyebrow}
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-[3rem] leading-[1.05] tracking-tight font-semibold text-neutral-900">
              {alsoLove.title}
            </h2>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button
              data-testid="also-love-prev"
              onClick={() => scrollByCards(-1)}
              disabled={!canPrev}
              aria-label="Previous"
              className={`w-12 h-12 rounded-full border border-neutral-300 flex items-center justify-center transition-all ${
                canPrev
                  ? "bg-white text-neutral-900 hover:bg-riversol-blue hover:border-riversol-blue hover:text-white"
                  : "opacity-40 cursor-not-allowed text-neutral-400"
              }`}
            >
              <ArrowLeft size={18} />
            </button>
            <button
              data-testid="also-love-next"
              onClick={() => scrollByCards(1)}
              disabled={!canNext}
              aria-label="Next"
              className={`w-12 h-12 rounded-full border border-neutral-300 flex items-center justify-center transition-all ${
                canNext
                  ? "bg-white text-neutral-900 hover:bg-riversol-blue hover:border-riversol-blue hover:text-white"
                  : "opacity-40 cursor-not-allowed text-neutral-400"
              }`}
            >
              <ArrowRight size={18} />
            </button>
          </div>
        </div>

        <div
          ref={trackRef}
          className="flex gap-4 lg:gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory cursor-grab select-none hide-scrollbar -mx-4 lg:-mx-8 px-4 lg:px-8 pb-4"
          style={{ scrollbarWidth: "none" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onPointerLeave={endDrag}
          onClickCapture={onClickCapture}
        >
          {alsoLove.products.map((p, i) => {
            const isBundle = p.tag?.toLowerCase().includes("bundle");
            const savings = (() => {
              if (!p.compareAt) return null;
              const a = parseFloat(String(p.compareAt).replace(/[^0-9.]/g, ""));
              const b = parseFloat(String(p.price).replace(/[^0-9.]/g, ""));
              if (!a || !b || a <= b) return null;
              const pct = Math.round(((a - b) / a) * 100);
              return { amount: (a - b).toFixed(0), pct };
            })();
            return (
            <article
              key={i}
              data-card
              data-testid={`also-love-card-${i}`}
              className={`group snap-start shrink-0 w-[260px] lg:w-[340px] flex flex-col transition-colors duration-300 relative ${
                isBundle
                  ? "border-2 border-riversol-blue bg-gradient-to-br from-riversol-sand via-white to-riversol-blue/[0.06] shadow-[0_20px_60px_-30px_rgba(11,37,69,0.35)]"
                  : "border border-neutral-200 bg-white hover:border-riversol-blue"
              }`}
            >
              {isBundle && (
                <div
                  data-testid={`also-love-bundle-ribbon-${i}`}
                  className="absolute -top-3 left-6 z-10 bg-riversol-blue text-white font-mono text-[10px] tracking-[0.18em] uppercase px-3 py-1.5 shadow-md"
                >
                  {p.tag}
                </div>
              )}
              <div className={`relative aspect-square overflow-hidden border-b ${isBundle ? "border-riversol-blue/20 bg-gradient-to-br from-riversol-sand to-white" : "border-neutral-200 bg-riversol-sand"}`} style={{ borderRadius: mediaRadius || "10px" }}>
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  draggable={false}
                />
                {p.tag && !isBundle && (
                  <span className="absolute top-3 left-3 bg-white/95 backdrop-blur px-3 py-1 font-mono text-[10px] tracking-[0.14em] uppercase text-riversol-blue">
                    {p.tag}
                  </span>
                )}
                {isBundle && savings && (
                  <span className="absolute top-3 right-3 bg-riversol-blue text-white font-mono text-[11px] tracking-[0.14em] uppercase px-3 py-1 rounded-full shadow">
                    Save ${savings.amount}
                  </span>
                )}
                <button
                  data-testid={`also-love-quick-add-${i}`}
                  className={`absolute bottom-3 right-3 w-10 h-10 rounded-full backdrop-blur flex items-center justify-center transition-colors shadow-sm ${
                    isBundle
                      ? "bg-riversol-blue text-white hover:bg-neutral-900"
                      : "bg-white/95 text-neutral-900 hover:bg-riversol-blue hover:text-white"
                  }`}
                  aria-label={`Add ${p.name}`}
                >
                  <Plus size={16} />
                </button>
              </div>

              <div className="p-6 flex flex-col gap-4 flex-1">
                <div className="flex items-center gap-2 text-[13px] text-neutral-500">
                  <span className="flex items-center gap-0.5 text-riversol-blue">
                    {[...Array(5)].map((_, s) => (
                      <Star
                        key={s}
                        size={12}
                        className={s < Math.round(p.rating) ? "fill-riversol-blue text-riversol-blue" : "text-neutral-300"}
                      />
                    ))}
                  </span>
                  <span className="font-mono text-[11px] text-neutral-500">
                    {p.rating.toFixed(1)} · {p.reviews.toLocaleString()}
                  </span>
                </div>

                <h3 className="text-lg text-neutral-900 leading-snug">{p.name}</h3>

                {p.variants?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {p.variants.map((v, vi) => (
                      <button
                        key={vi}
                        data-testid={`also-love-variant-${i}-${vi}`}
                        className={`px-3 py-1 border text-[11px] font-mono tracking-[0.08em] uppercase transition-colors ${
                          vi === 0
                            ? "border-riversol-blue text-riversol-blue bg-riversol-blue/[0.04]"
                            : "border-neutral-300 text-neutral-600 hover:border-neutral-900"
                        }`}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                )}

                {p.colors?.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-400">Shade</span>
                    <div className="flex items-center gap-1.5">
                      {p.colors.map((c, ci) => (
                        <button
                          key={ci}
                          data-testid={`also-love-color-${i}-${ci}`}
                          className={`w-5 h-5 rounded-full border ${ci === 0 ? "ring-2 ring-riversol-blue ring-offset-1 border-white" : "border-neutral-300"}`}
                          style={{ backgroundColor: c }}
                          aria-label={`Shade ${ci + 1}`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div className={`mt-auto pt-4 border-t flex items-baseline gap-2 ${isBundle ? "border-riversol-blue/20" : "border-neutral-100"}`}>
                  <span className={`font-mono text-lg ${isBundle ? "text-riversol-blue font-semibold" : "text-neutral-900"}`}>{p.price}</span>
                  {p.compareAt && (
                    <span className="font-mono text-sm text-neutral-400 line-through">{p.compareAt}</span>
                  )}
                  {isBundle && savings && (
                    <span className="ml-auto font-mono text-[11px] tracking-[0.12em] uppercase text-riversol-blue">
                      −{savings.pct}%
                    </span>
                  )}
                </div>
              </div>
            </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default YouMayAlsoLove;
